"use client";

import logger from "@/utils/logger";
/* eslint-disable @typescript-eslint/no-explicit-any */

import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
} from "react";
import { PublicClientApplication } from "@azure/msal-browser";
import {
    msalConfig,
    loginRequest,
    graphConfig,
    dispatchApiRequest,
} from "../config/msal-config";
import { useRouter } from "next/navigation";
import AuthService from "@/services/auth/auth-service";
import { UserApiService } from "@/services/api/internal-login/internal-user-api-service";
import { isDriverUser } from "@/utils/roleUtils";
import { syncAuthStateMarker } from "./authMarkerSync";
import {
    clearTokenData,
    getTokenData,
    setTokenData,
} from "@/services/tokenStorage";
import {
    ensureValidDispatchToken,
    forceRefreshDispatchToken,
    isDispatchAuthError,
} from "@/services/dispatchTokenManager";
import {
    clearMsalCacheArtifacts,
    clearStoredMsalSessionHint,
    getStoredMsalSessionHint,
    hasMsalCacheArtifacts,
    setStoredMsalSessionHint,
} from "@/services/msalSessionState";
import {
    clearStoredExternalUserProfile,
    getStoredExternalUserProfile,
    setStoredExternalUserProfile,
} from "@/services/webSessionStorage";
import {
    clearStoredInternalUserProfile,
    getStoredInternalUserProfile,
    setStoredInternalUserProfile,
} from "@/services/internalSessionProfileStorage";
import {
    collectMsalSessionSnapshot,
    requestMsalSessionSnapshot,
    subscribeToMsalSessionSnapshotRequests,
} from "@/services/msalTabSessionSync";
import {
    collectExternalSessionSnapshot,
    requestExternalSessionSnapshot,
    subscribeToExternalSessionSnapshotRequests,
} from "@/services/externalTabSessionSync";
import {
    hasRecoverableAuthHints,
    readAuthHintSnapshot,
} from "@/components/auth/authRecovery";
import {
    ACCOUNT_ACCESS_REVOKED_EVENT,
    ACCOUNT_INACTIVE_REASON,
    type AccountAccessState,
    applyAccountInactiveClientState,
    clearStoredLogoutEvent,
    clearStoredAccountAccessState,
    hasStoredLogoutEvent,
} from "@/services/accountAccessState";
import { isAccountInactiveApiError, parseApiError } from "@/utils/errorUtils";

const isTest =
    typeof process !== "undefined" &&
    process.env &&
    process.env.NODE_ENV === "test";
const EXTERNAL_SESSION_VALIDATION_INTERVAL_MS = 30_000;

// 🔥 UPDATED interface to include multiple roles and phone types
interface UserProfile {
    userId: number;
    displayName: string;
    firstName?: string;
    email: string;
    jobTitle?: string;
    officeLocation?: string;
    organization?: string;
    role?: string; // Keep for backward compatibility
    roles?: string[]; // 🔥 NEW: Multiple roles
    // NEW PROPERTIES for department functionality:
    isNewUser: boolean;
    organizationId?: number;
    department?: string;
    departmentId?: number;
    // 🔥 UPDATED: Separate mobile and business phone support
    mobilePhone?: string;
    businessPhones?: string[];
    primaryPhoneNumber?: string; // Keep for backward compatibility
    phoneType?: string; // Keep for backward compatibility
    // Profile photo support
    profilePhoto?: string; // Base64 encoded image from DB
    // Legacy fields for backward compatibility
    id?: string;
    name?: string;
    // Story 30332: resolved access-group scope (null = Admin / unrestricted)
    accessScope?: AccessScopeProfile | null;
}

// Story 30332: shape returned by the API alongside the user profile. Lets the client
// render scope-aware UI (picker preselects, SignalR routing) without hardcoding role
// names or request type ids.
export interface AccessScopeProfile {
    accessGroup?: string | null; // e.g. "CCS"
    scope?: "Coordinator" | "Requestor" | "Driver" | null;
    requestTypeIds: number[];
    restrictToOwnCreated: boolean;
    isDriverScoped: boolean;
}

// 🔥 UPDATED: External user profile interface now includes multiple roles and phone types
interface ExternalUserProfile {
    userId: number;
    displayName: string;
    email: string;
    jobTitle?: string;
    officeLocation?: string;
    organization?: string;
    role?: string; // Keep for backward compatibility
    roles?: string[]; // 🔥 NEW: Multiple roles
    isNewUser?: boolean;
    organizationId?: number;
    department?: string;
    departmentId?: number;
    // 🔥 UPDATED: Separate mobile and business phone support
    mobilePhone?: string;
    businessPhones?: string[];
    primaryPhoneNumber?: string; // Keep for backward compatibility
    phoneType?: string; // Keep for backward compatibility
    // Profile photo support
    profilePhoto?: string; // Base64 encoded image from DB
    isTemporaryPassword?: boolean;
    accessToken?: string;
    accessTokenExpiresAt?: string;
    refreshToken?: string | null;
    // Story 30332: resolved access-group scope (null = Admin / unrestricted)
    accessScope?: AccessScopeProfile | null;
    refreshTokenExpiresAt?: string | null;
    // Legacy fields for compatibility
    id?: string;
    name?: string;
}

// Interface for Microsoft Graph profile
interface GraphUserProfile {
    id: string;
    displayName?: string;
    givenName?: string;
    surname?: string;
    mail?: string;
    userPrincipalName?: string;
    jobTitle?: string;
    companyName?: string;
    department?: string;
    officeLocation?: string;
    mobilePhone?: string;
    businessPhones?: string[];
    homePhones?: string[]; // 🔥 NEW: Home phone numbers from Graph API
}

interface AuthContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    isProcessingRedirect: boolean; // 🔥 NEW: Flag to indicate MSAL redirect is being processed
    userProfile: UserProfile | null;
    externalUserProfile: ExternalUserProfile | null;
    loginType: "msal" | "external" | null;
    msalInstance: PublicClientApplication | null;
    login: () => Promise<void>;
    loginExternal: (userProfile: ExternalUserProfile) => Promise<void>;
    logout: (options?: { redirectTo?: string | null }) => Promise<void>;
    authError: string | null;
    clearAuthError: () => void;
    refreshUserProfile: () => Promise<UserProfile | ExternalUserProfile | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const DRIVER_ACCESS_ERROR_CODE = "DRIVER_USER_WEB_ACCESS_DENIED";
const DRIVER_ACCESS_FALLBACK_MESSAGE =
    "Driver accounts can only access the mobile driver application.";

const parseJsonObject = (input?: string | null) => {
    if (!input) return null;
    try {
        const parsed = JSON.parse(input);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
        return null;
    }
};

const extractErrorData = (error: unknown) => {
    const err = error as
        | {
              bodyText?: string;
              message?: string;
          }
        | undefined;

    const fromBodyText = parseJsonObject(err?.bodyText);
    if (fromBodyText) return fromBodyText;

    const rawMessage = typeof err?.message === "string" ? err.message : "";
    const directMessageJson = parseJsonObject(rawMessage);
    if (directMessageJson) return directMessageJson;

    // Handles wrapped errors like:
    // "MSAL login failed 400: { ...json payload... }"
    const wrappedJsonMatch = rawMessage.match(/\{[\s\S]*\}$/);
    if (wrappedJsonMatch) {
        return parseJsonObject(wrappedJsonMatch[0]);
    }

    return null;
};

const exposeMsalInstanceOnWindow = (
    instance: PublicClientApplication | null | undefined
) => {
    if (typeof window === "undefined" || !instance) {
        return;
    }

    (window as any).__msalInstance = instance;
};

const getCachedMsalAccount = (
    instance: PublicClientApplication
): ReturnType<PublicClientApplication["getActiveAccount"]> => {
    const activeAccount = instance.getActiveAccount();
    if (activeAccount) {
        return activeAccount;
    }

    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) {
        return null;
    }

    instance.setActiveAccount(accounts[0]);
    return accounts[0];
};

const createLegacyLocalStorageMsalConfig = () => ({
    ...msalConfig,
    cache: {
        ...msalConfig.cache,
        cacheLocation: "localStorage" as const,
        temporaryCacheLocation: "sessionStorage" as const,
    },
});

export function resolveMsalAuthProcessingError(
    error: unknown,
    fallbackMessage = "Failed to authenticate"
): {
    errorMessage: string;
    isDriverAccessDenied: boolean;
} {
    const errorData = extractErrorData(error);

    const combinedErrorText = [
        (errorData as any)?.message,
        (errorData as any)?.errorMessage,
        (errorData as any)?.details,
        error instanceof Error ? error.message : "",
    ]
        .filter(Boolean)
        .join(" ");

    const isDriverAccessDenied =
        (errorData as any)?.errorCode === DRIVER_ACCESS_ERROR_CODE ||
        (errorData as any)?.error === DRIVER_ACCESS_ERROR_CODE ||
        (errorData as any)?.code === DRIVER_ACCESS_ERROR_CODE ||
        /driver accounts can only access the mobile driver application/i.test(
            combinedErrorText
        );

    if (isDriverAccessDenied) {
        return {
            errorMessage:
                (errorData as any)?.message ||
                (errorData as any)?.errorMessage ||
                DRIVER_ACCESS_FALLBACK_MESSAGE,
            isDriverAccessDenied: true,
        };
    }

    if ((errorData as any)?.message || (errorData as any)?.errorMessage) {
        return {
            errorMessage:
                (errorData as any)?.message ||
                (errorData as any)?.errorMessage ||
                fallbackMessage,
            isDriverAccessDenied: false,
        };
    }

    if (error instanceof Error) {
        return { errorMessage: error.message, isDriverAccessDenied: false };
    }

    return { errorMessage: fallbackMessage, isDriverAccessDenied: false };
}

// 🔥 UPDATED: Helper function to add legacy fields for backward compatibility - now handles multiple roles and phone numbers
const addLegacyFields = (apiProfile: any): UserProfile => {
    return {
        ...apiProfile,
        // Add legacy fields for existing components
        id: apiProfile.userId?.toString() || "",
        name: apiProfile.displayName || "",
        firstName: apiProfile.firstName || "",
        // Ensure all new fields have default values if not provided by API
        isNewUser: apiProfile.isNewUser || false,
        organizationId: apiProfile.organizationId || null,
        department: apiProfile.department || null,
        departmentId: apiProfile.departmentId || null,
        // 🔥 EXPLICIT: Ensure phone fields are preserved from API response
        mobilePhone: apiProfile.mobilePhone || null,
        businessPhones: apiProfile.businessPhones || [],
        primaryPhoneNumber: apiProfile.primaryPhoneNumber || null,
        phoneType: apiProfile.phoneType || null,
        // 🔥 NEW: Handle multiple roles with fallback
        roles: apiProfile.roles || (apiProfile.role ? [apiProfile.role] : []),
        // Story 30332: pass through resolved access-group scope (null = Admin / unrestricted)
        accessScope: apiProfile.accessScope ?? null,
    };
};

// 🔥 UPDATED: Helper function to add legacy fields for external user profile - now handles multiple roles and phone numbers
const addLegacyFieldsExternal = (apiProfile: any): ExternalUserProfile => {
    return {
        ...apiProfile,
        // Add legacy fields for existing components
        id: apiProfile.userId?.toString() || "",
        name: apiProfile.displayName || "",
        // Ensure all new fields have default values if not provided by API
        isNewUser: apiProfile.isNewUser || false,
        organizationId: apiProfile.organizationId || null,
        department: apiProfile.department || null,
        departmentId: apiProfile.departmentId || null,
        // 🔥 EXPLICIT: Ensure phone fields are preserved from API response
        mobilePhone: apiProfile.mobilePhone || null,
        businessPhones: apiProfile.businessPhones || [],
        primaryPhoneNumber: apiProfile.primaryPhoneNumber || null,
        phoneType: apiProfile.phoneType || null,
        isTemporaryPassword: apiProfile.isTemporaryPassword || false,
        // 🔥 NEW: Handle multiple roles with fallback
        roles: apiProfile.roles || (apiProfile.role ? [apiProfile.role] : []),
        // Story 30332: pass through resolved access-group scope (null = Admin / unrestricted)
        accessScope: apiProfile.accessScope ?? null,
    };
};

//Provider
export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [msalInstance, setMsalInstance] =
        useState<PublicClientApplication | null>(null);
    const [authService, setAuthService] = useState<AuthService | null>(null);
    const [userApiService] = useState<UserApiService>(new UserApiService());
    const [isInitialized, setIsInitialized] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessingRedirect, setIsProcessingRedirect] = useState(false); // 🔥 NEW: Track redirect processing
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
    const [externalUserProfile, setExternalUserProfile] =
        useState<ExternalUserProfile | null>(null);
    const [loginType, setLoginType] = useState<"msal" | "external" | null>(
        null
    );
    const [authError, setAuthError] = useState<string | null>(null);
    const router = useRouter();

    const clearAuthError = () => setAuthError(null);

    const clearInMemoryAuthState = useCallback(
        (nextAuthError: string | null = null) => {
            sessionRestoreAttemptedRef.current = false;
            sessionRestoredRef.current = false;
            setSessionRestored(false);
            setIsAuthenticated(false);
            setUserProfile(null);
            setExternalUserProfile(null);
            setLoginType(null);
            setAuthError(nextAuthError);
            setIsLoading(false);
            setIsProcessingRedirect(false);

            if (userApiService) {
                (userApiService as any).cachedToken = null;
                (userApiService as any).tokenExpiry = null;
            }
        },
        [userApiService]
    );

    // Debug initial state
    useEffect(() => {
        logger.info("🔍 Auth Provider Initialized");
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const handleAccountAccessRevoked = (event: Event) => {
            const detail = (
                event as CustomEvent<AccountAccessState | undefined>
            ).detail;

            if (detail?.reason !== ACCOUNT_INACTIVE_REASON) {
                return;
            }

            clearInMemoryAuthState(detail.message);

            const redirectPath =
                typeof detail.redirectPath === "string" &&
                detail.redirectPath.trim()
                    ? detail.redirectPath
                    : "/403";

            if (window.location.pathname !== redirectPath) {
                router.replace(redirectPath);
            }
        };

        window.addEventListener(
            ACCOUNT_ACCESS_REVOKED_EVENT,
            handleAccountAccessRevoked as EventListener
        );

        return () => {
            window.removeEventListener(
                ACCOUNT_ACCESS_REVOKED_EVENT,
                handleAccountAccessRevoked as EventListener
            );
        };
    }, [clearInMemoryAuthState, router]);

    // 🔥 Expose MSAL instance on window for services that need it (e.g., admin phone endpoints)
    useEffect(() => {
        if (msalInstance && typeof window !== "undefined") {
            exposeMsalInstanceOnWindow(msalInstance);
            logger.info("✅ MSAL instance exposed on window");
        }
    }, [msalInstance]);

    // 🚨 EMERGENCY SESSION RESTORATION - Run this FIRST
    // 🔥 FIX: Use refs to prevent duplicate calls across React Strict Mode re-mounts
    const sessionRestoreAttemptedRef = useRef(false);
    const sessionRestoredRef = useRef(false);
    const [sessionRestored, setSessionRestored] = useState(false);

    useEffect(() => {
        // Prevent duplicate restoration attempts using ref (survives re-mounts)
        if (sessionRestoreAttemptedRef.current) {
            logger.info("🔍 Session restore already attempted, skipping...");
            return;
        }
        sessionRestoreAttemptedRef.current = true;

        const emergencySessionRestore = async () => {
            logger.info("🚨 Emergency session restore started...");

            try {
                if (hasStoredLogoutEvent()) {
                    logger.info(
                        "🔒 Explicit logout marker detected, skipping emergency restore"
                    );
                    setIsLoading(false);
                    return;
                }

                // Check for external user first
                const parsedUser =
                    getStoredExternalUserProfile<ExternalUserProfile>();
                if (parsedUser) {
                    if (parsedUser.userId && parsedUser.email) {
                        logger.info(
                            "✅ Emergency restore: External user found:",
                            parsedUser.displayName
                        );

                        if (!getTokenData()) {
                            const restoredFromSiblingTab =
                                await requestExternalSessionSnapshot(
                                    window.sessionStorage
                                );
                            if (restoredFromSiblingTab) {
                                logger.info(
                                    "✅ Emergency restore: External session token received from sibling tab"
                                );
                            }
                        }

                        try {
                            // Accept a still-valid cached Dispatch token on cold start.
                            // If it is expired, ensureValidDispatchToken will renew it.
                            await ensureValidDispatchToken();
                            clearStoredAccountAccessState();
                        } catch (restoreError) {
                            logger.info(
                                "⚠️ Emergency restore: External session validation failed:",
                                restoreError
                            );
                            clearTokenData();
                        }

                        if (!getTokenData()) {
                            // Skip restoring stale cached state when the backend no longer accepts the session.
                        } else {
                            clearStoredMsalSessionHint();
                            clearMsalCacheArtifacts(window.localStorage);
                            clearStoredInternalUserProfile();
                            const userWithLegacy =
                                addLegacyFieldsExternal(parsedUser);

                            // Set all state atomically to prevent flashing
                            setExternalUserProfile(userWithLegacy);
                            setUserProfile(null);
                            setLoginType("external");
                            setIsAuthenticated(true);
                            setAuthError(null);
                            setIsLoading(false);

                            // Fetch complete profile in background (non-blocking)
                            userApiService
                                .getExternalUserProfile(parsedUser.email)
                                .then((completeProfile) => {
                                    const completeProfileWithLegacy =
                                        addLegacyFieldsExternal(
                                            completeProfile
                                        );
                                    setExternalUserProfile(
                                        completeProfileWithLegacy
                                    );
                                    setStoredExternalUserProfile(
                                        completeProfileWithLegacy
                                    );
                                })
                                .catch((error) => {
                                    console.warn(
                                        "⚠️ Failed to fetch complete external profile in background:",
                                        error
                                    );
                                });
                            return;
                        }
                    }
                }

                let hasCurrentTabMsalData = hasMsalCacheArtifacts(
                    window.sessionStorage
                );
                const hasStoredMsalHint = Boolean(getStoredMsalSessionHint());
                const hasLegacyMsalData = hasMsalCacheArtifacts(
                    window.localStorage
                );
                const hasCrossTabAuthHints = hasRecoverableAuthHints(
                    readAuthHintSnapshot(window.localStorage, false)
                );

                if (
                    !hasCurrentTabMsalData &&
                    !hasLegacyMsalData &&
                    hasCrossTabAuthHints
                ) {
                    const restoredFromSiblingTab =
                        await requestMsalSessionSnapshot(window.sessionStorage);
                    if (restoredFromSiblingTab) {
                        logger.info(
                            "🔍 Emergency restore: Received MSAL session from sibling tab"
                        );
                        hasCurrentTabMsalData = hasMsalCacheArtifacts(
                            window.sessionStorage
                        );
                    }
                }

                let storedInternalProfile =
                    getStoredInternalUserProfile<UserProfile>();
                let dispatchToken = getTokenData();
                const hasSiblingAppSession = Boolean(
                    storedInternalProfile && dispatchToken
                );
                const hasRecoverableMsalState =
                    hasCurrentTabMsalData ||
                    hasStoredMsalHint ||
                    hasLegacyMsalData;

                if (!hasRecoverableMsalState && hasSiblingAppSession) {
                    logger.info(
                        "🔍 Emergency restore: Reusing internal app session from sibling tab"
                    );
                    try {
                        await ensureValidDispatchToken();
                        clearStoredAccountAccessState();
                        sessionRestoredRef.current = true;
                        setUserProfile(storedInternalProfile);
                        setExternalUserProfile(null);
                        setLoginType("msal");
                        setIsAuthenticated(true);
                        setAuthError(null);
                        setIsLoading(false);
                        setSessionRestored(true);
                        return;
                    } catch (restoreError) {
                        logger.info(
                            "⚠️ Emergency restore: Internal app session validation failed:",
                            restoreError
                        );
                        clearStoredInternalUserProfile();
                        clearTokenData();
                    }
                }

                if (hasRecoverableMsalState) {
                    logger.info(
                        "🔍 Emergency restore: Recoverable MSAL state found, attempting restore..."
                    );

                    try {
                        const emergencyMsal = new PublicClientApplication(
                            msalConfig
                        );
                        await emergencyMsal.initialize();
                        exposeMsalInstanceOnWindow(emergencyMsal);

                        let account = getCachedMsalAccount(emergencyMsal);
                        let tokenResponse: any = null;

                        if (account?.username) {
                            try {
                                tokenResponse =
                                    await emergencyMsal.acquireTokenSilent({
                                        ...loginRequest,
                                        account,
                                    });
                                setStoredMsalSessionHint(account);
                                clearMsalCacheArtifacts(window.localStorage);
                            } catch (sessionStorageRestoreError) {
                                logger.info(
                                    "⚠️ Session-storage MSAL restore skipped/failed:",
                                    sessionStorageRestoreError
                                );
                                clearMsalCacheArtifacts(window.sessionStorage);
                                account = null;
                            }
                        }

                        if (!tokenResponse && hasLegacyMsalData) {
                            try {
                                const legacyMsal = new PublicClientApplication(
                                    createLegacyLocalStorageMsalConfig()
                                );
                                await legacyMsal.initialize();

                                const legacyAccount =
                                    getCachedMsalAccount(legacyMsal);
                                if (legacyAccount?.username) {
                                    logger.info(
                                        "🔍 Emergency restore: Migrating legacy MSAL localStorage cache"
                                    );
                                    setStoredMsalSessionHint(legacyAccount);
                                    tokenResponse =
                                        await legacyMsal.acquireTokenSilent({
                                            ...loginRequest,
                                            account: legacyAccount,
                                        });
                                    const hydratedAccount =
                                        tokenResponse.account ?? legacyAccount;
                                    account = hydratedAccount;
                                    emergencyMsal.setActiveAccount(
                                        hydratedAccount
                                    );
                                    await emergencyMsal.hydrateCache(
                                        tokenResponse,
                                        {
                                            ...loginRequest,
                                            account: hydratedAccount,
                                        }
                                    );
                                    clearMsalCacheArtifacts(
                                        window.localStorage
                                    );
                                } else {
                                    clearMsalCacheArtifacts(
                                        window.localStorage
                                    );
                                }
                            } catch (legacyRestoreError) {
                                console.warn(
                                    "⚠️ Emergency MSAL legacy-cache migration failed:",
                                    legacyRestoreError
                                );
                                clearMsalCacheArtifacts(window.localStorage);
                            }
                        }

                        if (!tokenResponse) {
                            const storedMsalHint = getStoredMsalSessionHint();
                            if (storedMsalHint?.username) {
                                try {
                                    logger.info(
                                        "🔍 Emergency restore: Attempting MSAL silent SSO using stored login hint"
                                    );
                                    tokenResponse =
                                        await emergencyMsal.ssoSilent({
                                            ...loginRequest,
                                            loginHint: storedMsalHint.username,
                                        });
                                    account = tokenResponse.account;
                                    if (account) {
                                        emergencyMsal.setActiveAccount(account);
                                        setStoredMsalSessionHint(account);
                                    }
                                    clearMsalCacheArtifacts(
                                        window.localStorage
                                    );
                                } catch (silentRestoreError) {
                                    logger.info(
                                        "⚠️ Emergency MSAL silent SSO restore failed:",
                                        silentRestoreError
                                    );
                                    clearMsalCacheArtifacts(
                                        window.localStorage
                                    );
                                }
                            }
                        }

                        if (account && account.username && tokenResponse) {
                            logger.info(
                                "🔍 Emergency restore: MSAL account found:",
                                account.username
                            );
                            setStoredMsalSessionHint(account);

                            // Get Graph profile (including homePhones)
                            const graphResponse = await fetch(
                                `${graphConfig.graphMeEndpoint}?$select=id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,companyName,department,officeLocation,mobilePhone,businessPhones,homePhones`,
                                {
                                    headers: {
                                        Authorization: `Bearer ${tokenResponse.accessToken}`,
                                    },
                                }
                            );

                            if (graphResponse.ok) {
                                const graphProfile: GraphUserProfile =
                                    await graphResponse.json();
                                const email =
                                    graphProfile.mail ||
                                    graphProfile.userPrincipalName;

                                if (email) {
                                    // Get API profile
                                    const apiProfile =
                                        await userApiService.getUserProfile(
                                            email
                                        );
                                    const enhancedProfile = {
                                        ...apiProfile,
                                        firstName: graphProfile.givenName || "",
                                        // 🔥 MERGE: Ensure phone numbers from Graph are used if missing in API profile
                                        mobilePhone:
                                            graphProfile.mobilePhone ||
                                            apiProfile.mobilePhone,
                                        businessPhones:
                                            graphProfile.businessPhones &&
                                            graphProfile.businessPhones.length >
                                                0
                                                ? graphProfile.businessPhones
                                                : apiProfile.businessPhones,
                                    };

                                    const profileWithLegacy =
                                        addLegacyFields(enhancedProfile);
                                    setStoredInternalUserProfile(
                                        profileWithLegacy
                                    );
                                    clearStoredLogoutEvent();
                                    clearStoredAccountAccessState();

                                    // 🔥 FIX: Set ref FIRST to prevent race conditions
                                    sessionRestoredRef.current = true;

                                    // Set all state atomically to prevent flashing
                                    setUserProfile(profileWithLegacy);
                                    setExternalUserProfile(null);
                                    setLoginType("msal");
                                    setIsAuthenticated(true);
                                    setAuthError(null);
                                    setIsLoading(false);
                                    setMsalInstance(emergencyMsal);
                                    setAuthService(
                                        new AuthService(emergencyMsal)
                                    );
                                    setIsInitialized(true);
                                    setSessionRestored(true); // Mark session as restored (state for re-renders)

                                    logger.info(
                                        "✅ Emergency MSAL restore successful:",
                                        profileWithLegacy.displayName
                                    );
                                    return;
                                }
                            }
                        } else {
                            logger.info(
                                "🔍 Emergency restore: No recoverable MSAL account found"
                            );
                        }

                        storedInternalProfile =
                            getStoredInternalUserProfile<UserProfile>();
                        dispatchToken = getTokenData();
                        if (storedInternalProfile && dispatchToken) {
                            logger.info(
                                "🔍 Emergency restore: Reusing internal app session from sibling tab"
                            );
                            try {
                                await ensureValidDispatchToken();
                                clearStoredAccountAccessState();
                                sessionRestoredRef.current = true;
                                setUserProfile(storedInternalProfile);
                                setExternalUserProfile(null);
                                setLoginType("msal");
                                setIsAuthenticated(true);
                                setAuthError(null);
                                setIsLoading(false);
                                setSessionRestored(true);
                                return;
                            } catch (restoreError) {
                                logger.info(
                                    "⚠️ Emergency restore: Fallback internal app session validation failed:",
                                    restoreError
                                );
                                clearStoredInternalUserProfile();
                                clearTokenData();
                            }
                        }
                    } catch (msalError) {
                        console.error(
                            "❌ Emergency MSAL restore failed:",
                            msalError
                        );
                        // Do not clear shared MSAL storage from a transient tab-level failure.
                        // A sibling tab may still hold a healthy active session.
                    }

                    // If we get here, MSAL restore failed - set loading to false
                    setIsLoading(false);
                    return;
                }

                // If no session found
                logger.info("🔍 Emergency restore: No valid session found");
                setIsLoading(false);
            } catch (error) {
                console.error("❌ Emergency session restore failed:", error);
                setIsLoading(false);
            }
        };

        // Run immediately
        emergencySessionRestore();
    }, [userApiService]); // Run only once on mount - ref prevents duplicate execution

    // Initialize MSAL (only if not already initialized and not authenticated)
    useEffect(() => {
        // Don't initialize if we're already authenticated, initialized, or session was restored
        // Use both state and ref for checking - ref is synchronous, state is for React re-renders
        if (
            isTest ||
            isInitialized ||
            isAuthenticated ||
            sessionRestored ||
            sessionRestoredRef.current
        ) {
            logger.info(
                "🔍 MSAL init skipped - already initialized or session restored"
            );
            return;
        }

        let cancelled = false;

        async function initMsal() {
            // Double-check session wasn't restored while we were waiting
            if (
                sessionRestoredRef.current ||
                isAuthenticated ||
                isInitialized
            ) {
                logger.info(
                    "🔍 MSAL initialization skipped - session already restored"
                );
                return;
            }

            try {
                logger.info("🔍 Initializing MSAL (regular)...");
                const instance = new PublicClientApplication(msalConfig);
                await instance.initialize();

                // Double-check we still need to initialize
                if (
                    !cancelled &&
                    !isInitialized &&
                    !isAuthenticated &&
                    !sessionRestoredRef.current
                ) {
                    setMsalInstance(instance);
                    setAuthService(new AuthService(instance));
                    setIsInitialized(true);
                    logger.info("✅ MSAL initialized successfully");

                    // 🔥 FIX: Check if there's a pending MSAL redirect before setting loading to false
                    // If auth_in_progress flag is set, we're expecting a redirect result
                    const hasPendingRedirect =
                        typeof window !== "undefined" &&
                        (sessionStorage.getItem("auth_in_progress") ===
                            "true" ||
                            window.location.href.includes("code=") ||
                            window.location.hash.includes("code="));

                    // Only set loading to false if we're not authenticated AND there's no pending redirect
                    if (!isAuthenticated && !hasPendingRedirect) {
                        setIsLoading(false);
                    } else if (hasPendingRedirect) {
                        logger.info(
                            "🔍 Pending MSAL redirect detected, keeping loading state"
                        );
                    }
                } else {
                    logger.info(
                        "🔍 MSAL initialization skipped - already authenticated or restored"
                    );
                }
            } catch (err: any) {
                console.error("❌ MSAL initialization error:", err);
                if (!cancelled && !isAuthenticated) {
                    setAuthError("Failed to initialize MSAL");
                    setIsLoading(false);
                }
            }
        }

        // Delay to allow emergency restore to complete first
        const timer = setTimeout(initMsal, 500); // Increased from 300ms to 500ms

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [isInitialized, isAuthenticated, sessionRestored]);

    // Function to process user after MSAL authentication
    // 🔥 FIX: Memoize to prevent unnecessary dependency updates
    const processUserAfterMsalAuth = useCallback(
        async (
            account: any,
            tokenResponse: any,
            instanceOverride?: PublicClientApplication | null
        ) => {
            // Guard against duplicate processing
            if (isAuthenticated) {
                logger.info("⚠️ User already authenticated, skipping process");
                return;
            }

            try {
                logger.info(
                    "🔐 Processing user after MSAL auth for:",
                    account.username
                );
                setStoredMsalSessionHint(account);

                // Get the user profile from Microsoft Graph (including homePhones)
                const graphResponse = await fetch(
                    `${graphConfig.graphMeEndpoint}?$select=id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,companyName,department,officeLocation,mobilePhone,businessPhones,homePhones`,
                    {
                        headers: {
                            Authorization: `Bearer ${tokenResponse.accessToken}`,
                        },
                    }
                );

                if (!graphResponse.ok) {
                    throw new Error(
                        `Failed to fetch user profile from Microsoft Graph: ${graphResponse.status}`
                    );
                }

                const graphProfile: GraphUserProfile =
                    await graphResponse.json();

                const dispatchExchangeMsalInstance =
                    instanceOverride ?? msalInstance;

                if (!dispatchExchangeMsalInstance) {
                    throw new Error(
                        "MSAL instance unavailable for Dispatch API exchange"
                    );
                }

                exposeMsalInstanceOnWindow(dispatchExchangeMsalInstance);

                const dispatchApiTokenResponse =
                    await dispatchExchangeMsalInstance.acquireTokenSilent({
                        ...dispatchApiRequest,
                        account,
                    });

                // Call your API to save/update user data using the Dispatch API AAD access token
                const apiUserProfile = await userApiService.msalLogin(
                    graphProfile,
                    dispatchApiTokenResponse.accessToken
                );

                // Extract firstName from Graph profile and add to API profile
                const enhancedApiProfile = {
                    ...apiUserProfile,
                    firstName: graphProfile.givenName || "",
                    // 🔥 MERGE: Ensure phone numbers from Graph are used if missing in API profile
                    mobilePhone:
                        graphProfile.mobilePhone || apiUserProfile.mobilePhone,
                    businessPhones:
                        graphProfile.businessPhones &&
                        graphProfile.businessPhones.length > 0
                            ? graphProfile.businessPhones
                            : apiUserProfile.businessPhones,
                };
                const profileWithLegacy = addLegacyFields(enhancedApiProfile);
                setStoredInternalUserProfile(profileWithLegacy);
                clearStoredLogoutEvent();
                clearStoredAccountAccessState();

                // 🔥 NEW: Save Dispatch JWT for BaseService
                if (
                    apiUserProfile.accessToken &&
                    apiUserProfile.accessTokenExpiresAt
                ) {
                    const expiresAt = new Date(
                        apiUserProfile.accessTokenExpiresAt
                    ).getTime();

                    if (!Number.isNaN(expiresAt)) {
                        setTokenData({
                            accessToken: apiUserProfile.accessToken,
                            refreshToken: "",
                            expiresAt,
                        });
                    } else {
                        console.warn(
                            "⚠️ Unable to parse Dispatch token expiry for internal user"
                        );
                    }
                } else {
                    console.warn(
                        "⚠️ Internal login did not include Dispatch token; requests may fail"
                    );
                }

                // Set all authentication state atomically to prevent flashing
                setUserProfile(profileWithLegacy);
                setExternalUserProfile(null);
                setLoginType("msal");
                setIsAuthenticated(true);
                setAuthError(null);
                setIsLoading(false);

                // Clear any existing external user data
                clearStoredExternalUserProfile();
                clearMsalCacheArtifacts(window.localStorage);

                logger.info(
                    "✅ MSAL authentication successful for:",
                    profileWithLegacy.displayName
                );
            } catch (error: any) {
                console.error(
                    "❌ Error processing user after MSAL auth:",
                    error
                );

                if (
                    error instanceof Error &&
                    isAccountInactiveApiError(error.message)
                ) {
                    const inactiveMessage = parseApiError(error.message);
                    applyAccountInactiveClientState(inactiveMessage);
                    setAuthError(inactiveMessage);
                    setIsLoading(false);
                    router.replace("/403");
                    return;
                }

                const { errorMessage, isDriverAccessDenied } =
                    resolveMsalAuthProcessingError(
                        error,
                        error instanceof Error
                            ? parseApiError(error.message)
                            : "Failed to authenticate"
                    );

                if (isDriverAccessDenied) {
                    logger.info(
                        "🚫 Driver access restriction detected. Setting partial profile for DriverAccessBlock."
                    );

                    // Set a partial profile with the driver role so DriverAccessBlock can trigger.
                    const restrictedProfile = {
                        displayName: account.name || account.username,
                        email: account.username,
                        roles: ["Driver"],
                        isNewUser: false,
                    };
                    setUserProfile(addLegacyFields(restrictedProfile));
                    setLoginType("msal");
                }

                setAuthError(errorMessage);
                setIsLoading(false);
            }
        },
        [userApiService, isAuthenticated, msalInstance, router]
    );

    // Handle back button to cancel auth flow
    useEffect(() => {
        const handlePopState = () => {
            logger.info("🔙 Back button detected, cleaning up auth state");
            // Clear the auth in progress flag
            sessionStorage.removeItem("auth_in_progress");
            // Reset loading state
            setIsLoading(false);
        };

        window.addEventListener("popstate", handlePopState);
        return () => window.removeEventListener("popstate", handlePopState);
    }, []);

    // Handle redirect and check login status (only for new logins)
    // 🔥 FIX: Remove isAuthenticated from dependency array to prevent re-running after successful auth
    // 🔥 FIX: Use useCallback for processUserAfterMsalAuth and only include userApiService in deps
    useEffect(() => {
        // Don't handle redirects if MSAL not ready, already initialized, or session was restored
        if (
            !msalInstance ||
            !isInitialized ||
            sessionRestored ||
            sessionRestoredRef.current
        ) {
            logger.info(
                "🔍 handleMsalRedirect skipped - conditions not met or session restored"
            );
            return;
        }

        let cancelled = false;

        async function handleMsalRedirect() {
            // Double-check session wasn't restored
            if (sessionRestoredRef.current) {
                logger.info(
                    "🔍 handleMsalRedirect skipped - session already restored"
                );
                return;
            }

            // 🔥 FIX: Set processing flag at the start
            setIsProcessingRedirect(true);

            try {
                logger.info("🔍 Checking for MSAL redirect result...");
                if (!msalInstance) {
                    setAuthError("MSAL instance is not available.");
                    setIsLoading(false);
                    setIsProcessingRedirect(false);
                    // 🔥 FIX: Clear the pending redirect flag on error
                    sessionStorage.removeItem("auth_in_progress");
                    return;
                }

                const redirectResult =
                    await msalInstance.handleRedirectPromise();

                if (cancelled || sessionRestoredRef.current) {
                    setIsProcessingRedirect(false);
                    return;
                }

                if (redirectResult && redirectResult.account) {
                    logger.info(
                        "✅ Handling redirect result, found active account"
                    );
                    // 🔥 FIX: Clear the pending redirect flag immediately when we have a result
                    sessionStorage.removeItem("auth_in_progress");
                    msalInstance.setActiveAccount(redirectResult.account);
                    await processUserAfterMsalAuth(
                        redirectResult.account,
                        redirectResult,
                        msalInstance
                    );
                    // 🔥 FIX: Clear processing flag after successful auth
                    setIsProcessingRedirect(false);
                } else {
                    logger.info("🔍 No MSAL redirect result found");
                    // 🔥 FIX: Clear the pending redirect flag when no result found
                    sessionStorage.removeItem("auth_in_progress");

                    // Recover from tab races where active account is not set yet.
                    const allAccounts = msalInstance.getAllAccounts();
                    if (!cancelled && allAccounts.length > 0) {
                        const fallbackAccount =
                            msalInstance.getActiveAccount() || allAccounts[0];
                        msalInstance.setActiveAccount(fallbackAccount);

                        try {
                            logger.info(
                                "🔍 Attempting silent MSAL session restore using cached account"
                            );
                            const tokenResponse =
                                await msalInstance.acquireTokenSilent({
                                    ...loginRequest,
                                    account: fallbackAccount,
                                });
                            await processUserAfterMsalAuth(
                                fallbackAccount,
                                tokenResponse,
                                msalInstance
                            );
                        } catch (silentRestoreError) {
                            logger.info(
                                "⚠️ Silent MSAL restore skipped/failed:",
                                silentRestoreError
                            );
                        }
                    }

                    setIsProcessingRedirect(false);
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                }
            } catch (err: any) {
                console.error("Auth handling error:", err);
                // 🔥 FIX: Clear the pending redirect flag on error
                sessionStorage.removeItem("auth_in_progress");
                setIsProcessingRedirect(false);
                if (!cancelled) {
                    setAuthError("Authentication error occurred");
                    setIsLoading(false);
                }
            }
        }

        handleMsalRedirect();
        return () => {
            cancelled = true;
        };
    }, [
        msalInstance,
        isInitialized,
        processUserAfterMsalAuth,
        sessionRestored,
    ]);

    // Cross-tab synchronization
    useEffect(() => {
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "externalUserProfile") {
                if (e.newValue) {
                    // External user logged in from another tab
                    try {
                        const parsedUser = JSON.parse(e.newValue);
                        const userWithLegacy =
                            addLegacyFieldsExternal(parsedUser);

                        // Only update if we don't already have this user
                        if (
                            !externalUserProfile ||
                            externalUserProfile.userId !== userWithLegacy.userId
                        ) {
                            setExternalUserProfile(userWithLegacy);
                            setUserProfile(null);
                            setLoginType("external");
                            setIsAuthenticated(true);
                            setIsLoading(false);
                            logger.info(
                                "✅ Synced external login from another tab"
                            );
                        }
                    } catch (error) {
                        console.error(
                            "❌ Error syncing external login:",
                            error
                        );
                    }
                } else {
                    // External user logged out from another tab
                    if (loginType === "external") {
                        setExternalUserProfile(null);
                        setIsAuthenticated(false);
                        setLoginType(null);
                        setIsLoading(false);
                        logger.info(
                            "✅ Synced external logout from another tab"
                        );
                    }
                }
            }
        };

        window.addEventListener("storage", handleStorageChange);
        return () => window.removeEventListener("storage", handleStorageChange);
    }, [loginType, externalUserProfile]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        return subscribeToExternalSessionSnapshotRequests(() => {
            if (
                loginType !== "external" ||
                !isAuthenticated ||
                !externalUserProfile ||
                isLoading ||
                isProcessingRedirect ||
                hasStoredLogoutEvent()
            ) {
                return null;
            }

            return collectExternalSessionSnapshot(window.sessionStorage);
        });
    }, [
        externalUserProfile,
        isAuthenticated,
        isLoading,
        isProcessingRedirect,
        loginType,
    ]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        return subscribeToMsalSessionSnapshotRequests(() => {
            if (
                loginType === "external" ||
                externalUserProfile ||
                !isAuthenticated ||
                loginType !== "msal" ||
                !userProfile ||
                !getTokenData() ||
                !getStoredInternalUserProfile()
            ) {
                return null;
            }

            const snapshot = collectMsalSessionSnapshot(window.sessionStorage);
            if (!snapshot) {
                return null;
            }

            return snapshot;
        });
    }, [externalUserProfile, isAuthenticated, loginType, userProfile]);

    // Keep auth-state marker in sync to avoid transient guard mismatches.
    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        syncAuthStateMarker(localStorage, {
            isAuthenticated,
            isLoading,
            isProcessingRedirect,
            loginType,
        });
    }, [isAuthenticated, loginType, isLoading, isProcessingRedirect]);

    useEffect(() => {
        if (
            typeof window === "undefined" ||
            loginType !== "external" ||
            !isAuthenticated ||
            !externalUserProfile ||
            isLoading ||
            isProcessingRedirect
        ) {
            return;
        }

        let validationInFlight = false;

        const validateExternalSession = async (
            source: "focus" | "interval" | "visibility"
        ) => {
            if (validationInFlight) {
                return;
            }

            if (
                source === "interval" &&
                document.visibilityState === "hidden"
            ) {
                return;
            }

            validationInFlight = true;

            try {
                await forceRefreshDispatchToken();
                clearStoredAccountAccessState();
            } catch (error) {
                if (
                    isDispatchAuthError(error) &&
                    error.code === "ACCOUNT_INACTIVE"
                ) {
                    return;
                }

                logger.warn(
                    "⚠️ External session validation failed without revocation",
                    {
                        source,
                        error,
                    }
                );
            } finally {
                validationInFlight = false;
            }
        };

        const intervalId = window.setInterval(() => {
            void validateExternalSession("interval");
        }, EXTERNAL_SESSION_VALIDATION_INTERVAL_MS);

        const handleFocus = () => {
            void validateExternalSession("focus");
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                void validateExternalSession("visibility");
            }
        };

        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.clearInterval(intervalId);
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, [
        externalUserProfile,
        isAuthenticated,
        isLoading,
        isProcessingRedirect,
        loginType,
    ]);

    // MSAL Login function
    const login = async () => {
        try {
            if (!msalInstance) {
                throw new Error("MSAL not initialized");
            }
            setAuthError(null);
            await msalInstance.loginRedirect(loginRequest);
        } catch (err: any) {
            console.error("Login failed:", err);

            // If it's an interaction_in_progress error, clear the session and show helpful message
            if (
                err?.errorCode === "interaction_in_progress" ||
                err?.message?.includes("interaction_in_progress")
            ) {
                logger.info(
                    "🔄 Interaction already in progress, clearing cache..."
                );
                try {
                    // Only clear session-specific interaction state, not all MSAL data
                    const cacheKeys = Object.keys(sessionStorage);
                    cacheKeys.forEach((key) => {
                        if (
                            key.startsWith("msal.") &&
                            key.includes("request")
                        ) {
                            sessionStorage.removeItem(key);
                        }
                    });
                    setAuthError(
                        "Login session conflict. Please refresh the page and try again."
                    );
                } catch (clearErr) {
                    console.error("Error clearing cache:", clearErr);
                    setAuthError("Please refresh the page and try again.");
                }
            } else {
                setAuthError("Login failed");
            }
            throw err;
        }
    };

    // Enhanced external login function
    const loginExternal = async (userProfile: ExternalUserProfile) => {
        try {
            logger.info("🔍 Setting external user login:", userProfile);

            const userWithLegacy = addLegacyFieldsExternal(userProfile);
            const isDriver = isDriverUser(userWithLegacy.roles);

            if (userProfile.accessToken && userProfile.accessTokenExpiresAt) {
                const expiresAt = new Date(
                    userProfile.accessTokenExpiresAt
                ).getTime();

                if (!Number.isNaN(expiresAt)) {
                    setTokenData({
                        accessToken: userProfile.accessToken,
                        refreshToken: "",
                        expiresAt,
                    });
                } else {
                    console.warn(
                        "⚠️ Unable to parse Dispatch token expiry for external user"
                    );
                }
            } else {
                console.warn(
                    "⚠️ External login did not include Dispatch token; requests may fail"
                );
            }

            // Set all state immediately to ensure UI updates
            setExternalUserProfile(userWithLegacy);
            setUserProfile(null);
            setLoginType("external");

            // Only set authenticated if NOT a driver
            if (!isDriver) {
                setIsAuthenticated(true);
                setAuthError(null);
                logger.info("✅ External user authenticated");
            } else {
                setIsAuthenticated(false);
                setAuthError(
                    "Driver accounts can only access the mobile driver application."
                );
                logger.info(
                    "🚫 External driver detected, keeping unauthenticated for DriverAccessBlock"
                );
            }

            setIsLoading(false);

            clearStoredMsalSessionHint();
            clearStoredInternalUserProfile();
            clearStoredLogoutEvent();
            setStoredExternalUserProfile(userWithLegacy);
            clearStoredAccountAccessState();

            logger.info("✅ External user state set immediately");

            // Fetch complete profile in background
            if (userProfile.email) {
                userApiService
                    .getExternalUserProfile(userProfile.email)
                    .then((completeProfile) => {
                        const completeProfileWithLegacy =
                            addLegacyFieldsExternal(completeProfile);
                        setExternalUserProfile(completeProfileWithLegacy);
                        setStoredExternalUserProfile(completeProfileWithLegacy);
                        logger.info(
                            "✅ External user complete profile loaded in background"
                        );
                    })
                    .catch((apiError) => {
                        console.error(
                            "❌ Failed to fetch complete external user profile:",
                            apiError
                        );
                    });
            }

            logger.info("✅ External user login successful");
        } catch (error) {
            console.error("❌ Error setting external user login:", error);
            setAuthError("Failed to set external user login");
            setIsLoading(false);
        }
    };

    // Enhanced refresh user profile function
    const refreshUserProfile = async (): Promise<
        UserProfile | ExternalUserProfile | null
    > => {
        try {
            logger.info("Refreshing user profile from API...", { loginType });

            if (loginType === "external") {
                const email = externalUserProfile?.email;
                if (!email) {
                    throw new Error(
                        "No email available for external user profile refresh"
                    );
                }

                const profile =
                    await userApiService.getExternalUserProfile(email);
                const profileWithLegacy = addLegacyFieldsExternal(profile);
                setExternalUserProfile(profileWithLegacy);
                setAuthError(null);
                setStoredExternalUserProfile(profileWithLegacy);
                return profileWithLegacy;
            }

            // MSAL user refresh
            let email = userProfile?.email;
            let graphPhoneData: {
                mobilePhone?: string;
                businessPhones?: string[];
            } = {};

            if (msalInstance) {
                const account = msalInstance.getActiveAccount();
                if (account) {
                    try {
                        const tokenResponse =
                            await msalInstance.acquireTokenSilent({
                                ...loginRequest,
                                account,
                            });

                        const graphResponse = await fetch(
                            `${graphConfig.graphMeEndpoint}?$select=id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,companyName,department,officeLocation,mobilePhone,businessPhones,homePhones`,
                            {
                                headers: {
                                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                                },
                            }
                        );

                        if (graphResponse.ok) {
                            const graphProfile: GraphUserProfile =
                                await graphResponse.json();
                            email =
                                email ||
                                graphProfile.mail ||
                                graphProfile.userPrincipalName;
                            graphPhoneData = {
                                mobilePhone: graphProfile.mobilePhone,
                                businessPhones: graphProfile.businessPhones,
                            };
                        }
                    } catch (e) {
                        console.warn(
                            "Failed to fetch Graph data during refresh",
                            e
                        );
                    }
                }
            }

            if (!email) {
                throw new Error("No email available for profile refresh");
            }

            // 🔥 FIX: Force refresh to bypass cache
            const profile = await userApiService.getUserProfile(email, true);
            const profileWithLegacy = addLegacyFields(profile);

            // Merge Graph phone data if available and missing in API profile
            if (graphPhoneData.mobilePhone && !profileWithLegacy.mobilePhone) {
                profileWithLegacy.mobilePhone = graphPhoneData.mobilePhone;
            }
            if (
                graphPhoneData.businessPhones &&
                graphPhoneData.businessPhones.length > 0 &&
                (!profileWithLegacy.businessPhones ||
                    profileWithLegacy.businessPhones.length === 0)
            ) {
                profileWithLegacy.businessPhones =
                    graphPhoneData.businessPhones;
            }

            setUserProfile(profileWithLegacy);
            setStoredInternalUserProfile(profileWithLegacy);
            setAuthError(null);
            return profileWithLegacy;
        } catch (err) {
            console.error("Failed to refresh user profile:", err);
            setAuthError("Failed to refresh user profile");
            throw err;
        }
    };

    // Enhanced logout function
    const logout = async (options?: { redirectTo?: string | null }) => {
        try {
            if (loginType === "msal") {
                if (!authService) {
                    throw new Error("Auth service not initialized");
                }
                await authService.logout();
            } else if (loginType === "external") {
                clearStoredExternalUserProfile();
            }

            // Reset all state
            clearTokenData();
            clearStoredInternalUserProfile();
            clearMsalCacheArtifacts(window.sessionStorage);
            clearMsalCacheArtifacts(window.localStorage);
            clearStoredMsalSessionHint();
            clearStoredExternalUserProfile();
            clearStoredAccountAccessState();
            window.sessionStorage.removeItem("auth_in_progress");
            clearInMemoryAuthState();

            try {
                localStorage.removeItem("timecard_display_lock");
                localStorage.removeItem("timecard_display_lock_heartbeat");
            } catch {
                // Ignore localStorage failures in restricted environments.
            }

            const redirectTo =
                options?.redirectTo === undefined ? "/" : options.redirectTo;
            if (redirectTo) {
                router.push(redirectTo);
            }
        } catch (err) {
            console.error("Logout failed:", err);
            setAuthError("Logout failed");
            throw err;
        }
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                isLoading,
                isProcessingRedirect, // 🔥 NEW: Expose redirect processing state
                userProfile,
                externalUserProfile,
                loginType,
                msalInstance,
                login,
                loginExternal,
                logout,
                authError,
                clearAuthError,
                refreshUserProfile,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
